import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { SearchTypeaheadDTO } from "pages/reports/bi-dashboard-next/models/SearchTypeaheadDTO";
import getLocalApiUrl from "pages/reports/utils/getLocalApiUrl";

interface UseSearchTypeaheadOptions {
  debounceMs?: number;
  minSearchLength?: number;
}

export const useSearchTypeahead = (
  searchTerm: string,
  options: UseSearchTypeaheadOptions = {},
) => {
  const { debounceMs = 300, minSearchLength = 2 } = options;
  const { t } = useTranslation("reports");

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<SearchTypeaheadDTO | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const apiUrl = useMemo(
    () => getLocalApiUrl("/reporting/reports/search-typeahead"),
    [],
  );

  useEffect(() => {
    if (!searchTerm || searchTerm.length < minSearchLength) {
      setData(null);
      setIsLoading(false);
      return;
    }

    const fetchTypeahead = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get<SearchTypeaheadDTO>(apiUrl, {
          params: {
            query: searchTerm,
          },
        });
        setData(response.data);
      } catch (err) {
        console.error("Error fetching typeahead data:", err);
        setError(
          err instanceof Error ? err : new Error(t("reports:Unknown error")),
        );
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchTypeahead();
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, apiUrl, debounceMs, minSearchLength]);

  return { isLoading, data, error };
};
